import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { QrCode, Flashlight, FlashlightOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { useData } from "@/contexts/DataContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { normalizeRedeemCode } from "@/lib/utils";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

// Simplified redeemer view focusing on today's redemptions
const RedeemerDashboard = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { actions, campaigns } = useData();
  const [today, setToday] = useState(null);
  const [detailFor, setDetailFor] = useState(null); // action object
  const [detailTab, setDetailTab] = useState('today'); // 'today' | 'all'
  const [pageToday, setPageToday] = useState(0);
  const [pageAll, setPageAll] = useState(0);
  const [pageSize] = useState(10);
  const [listToday, setListToday] = useState({ items: [], total: 0 });
  const [listAll, setListAll] = useState({ items: [], total: 0 });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const trackRef = useRef(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [scanInfo, setScanInfo] = useState(null); // { valid, redeemable, redeemed, reason, discountType, discountValue, actionTitle }
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  // Bestätigungs-Popup nach Scan/Eingabe
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState(null); // { code, ...status }
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [showRedeemSuccessAnim, setShowRedeemSuccessAnim] = useState(false);
  const [redeemAnimFading, setRedeemAnimFading] = useState(false);
  const dateStr = new Date().toISOString().substring(0, 10);

  useEffect(() => {
    if (!showRedeemSuccessAnim) return;
    setRedeemAnimFading(false);
    const fadeTimer = setTimeout(() => {
      setRedeemAnimFading(true);
    }, 6500);
    const timeoutId = setTimeout(() => {
      setShowRedeemSuccessAnim(false);
      setRedeemAnimFading(false);
    }, 7000);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(timeoutId);
    };
  }, [showRedeemSuccessAnim]);

  const checkCodeAndOpenConfirm = async (rawCode) => {
    const trimmed = normalizeRedeemCode(rawCode);
    if (!trimmed || !token) return;
    const s = await api.codeStatus(token, trimmed);
    setScanInfo(s);
    setConfirmData({ code: trimmed, ...s });
    setShowRedeemSuccessAnim(false);
    setConfirmOpen(true);
    return s;
  };
  useEffect(() => {
    if (!token) return;
    (async () => {
      const d = await api.analyticsDay(token, dateStr);
      setToday(d);
    })();
  }, [token, dateStr]);

  // Load detail lists (Einlösungen) when modal open or page changes
  useEffect(() => {
    if (!token || !detailFor) return;
    (async () => {
      const todayRes = await api.actionRedeems(token, detailFor.id, { date: dateStr, limit: pageSize, offset: pageToday * pageSize });
      setListToday(todayRes);
      const allRes = await api.actionRedeems(token, detailFor.id, { limit: pageSize, offset: pageAll * pageSize });
      setListAll(allRes);
    })();
  }, [token, detailFor, pageToday, pageAll, pageSize, dateStr]);

  const performRedeem = async (redeemCode) => {
    const trimmed = normalizeRedeemCode(redeemCode || code);
    if (!trimmed) return;
    setConfirmBusy(true);
    try {
      const res = await api.redeemEmployee(token, trimmed);
      const isStamp = !!res?.stampCard;
      const stampIntent = res?.stampIntent || confirmData?.stampIntent || null;
      const isStampRewardRedeem = isStamp && stampIntent === 'REWARD_REDEEM';
      toast({
        title: isStamp
          ? (isStampRewardRedeem ? 'Belohnung eingelöst' : 'Stempelkarte bestätigt')
          : t('redeemer.successTitle'),
        description: isStamp
          ? (isStampRewardRedeem
              ? `Belohnung eingelöst: ${res?.stampReward || res?.discountValue || '-'}`
              : (res?.collected
              ? `Fortschritt: ${res?.stampProgressBefore ?? "-"}/${res?.total ?? "-"} -> ${res?.stampProgressAfter ?? "-"}/${res?.total ?? "-"}`
              : (res?.redeemed ? `Belohnung eingelöst: ${res?.discountValue ?? ''}` : undefined)))
          : (res?.discountType ? t('redeemer.successDesc', { type: res.discountType, value: (res.discountValue ?? '') }) : undefined),
      });
      setCode("");
      setShowRedeemSuccessAnim(true);
      // Update confirm data and stats
      setConfirmData((d) => {
        if (!d) return d;
        if (isStamp) {
          return {
            ...d,
            redeemable: false,
            redeemed: !!res?.redeemed,
            redeemedAt: res?.redeemedAt || d.redeemedAt || null,
            discountType: res?.discountType ?? d.discountType,
            discountValue: res?.discountValue ?? d.discountValue,
            stampReward: res?.stampReward ?? d.stampReward,
            stampProgressBefore: res?.stampProgressBefore ?? d.stampProgressBefore,
            stampProgressAfterPreview: res?.stampProgressAfter ?? d.stampProgressAfterPreview,
            stampTotal: res?.total ?? d.stampTotal,
            reason: res?.collected
              ? 'Stempel wurde verbucht'
              : (res?.redeemed ? 'Belohnung wurde eingelöst' : d.reason),
          };
        }
        return { ...d, redeemable: false, redeemed: true, redeemedAt: new Date().toISOString() };
      });
      try {
        const d = await api.analyticsDay(token, dateStr);
        setToday(d);
      } catch (_) {}
    } catch (err) {
      toast({ title: t('redeemer.redeemFailed'), description: err?.message || t('redeemer.unknownError') });
    } finally {
      setConfirmBusy(false);
    }
  };

  const openConfirm = async (e) => {
    e?.preventDefault?.();
    const trimmed = normalizeRedeemCode(code);
    if (!trimmed || !token) return;
    setLoading(true);
    try {
      await checkCodeAndOpenConfirm(trimmed);
    } catch (err) {
      setScanInfo({ valid: false, error: err?.message || t('redeemer.invalid') });
      toast({ title: t('redeemer.invalidCode'), description: err?.message || "" });
    } finally {
      setLoading(false);
    }
  };
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setCode(normalizeRedeemCode(text));
    } catch (err) {
      toast({ title: t('redeemer.pasteNotPossible'), description: t('redeemer.clipboardNoAccess') });
    }
  };
  const onBlurNormalize = () => {
    if (!code) return;
    const n = normalizeRedeemCode(code);
    if (n !== code) setCode(n);
  };
  // Fullscreen scanner: only active when scannerOpen
  useEffect(() => {
    let stream;
    let rafId;
    let mounted = true;
    if (!scannerOpen) return;
    async function startCamera() {
      try {
        const v = videoRef.current;
        if (!v) return;
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        v.srcObject = stream;
        await v.play();
        setScanning(true);
        const track = stream.getVideoTracks?.()[0];
        trackRef.current = track || null;
        try {
          const caps = track?.getCapabilities?.();
          setHasTorch(!!caps?.torch);
        } catch {}
        loop();
      } catch (err) {
        setCameraError(t('redeemer.cameraUnavailable') + (err?.message ? `: ${err.message}` : ""));
      }
    }
    async function loop() {
      if (!mounted) return;
      const v = videoRef.current;
      const c = canvasRef.current;
      if (v && c && v.readyState === 4) {
        const w = v.videoWidth;
        const h = v.videoHeight;
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.drawImage(v, 0, 0, w, h);
        try {
          const mod = await import('jsqr');
          const jsQR = mod && (mod.default || mod);
          const img = ctx.getImageData(0, 0, w, h);
          const qr = jsQR ? jsQR(img.data, img.width, img.height) : null;
          if (qr && qr.data) {
            let value = String(qr.data || '').trim();
            value = normalizeRedeemCode(value);
            if (value) {
              setCode(value);
              setScannerOpen(false); // close overlay
              try { navigator.vibrate?.(80); } catch {}
              setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 60);
              // Fetch status for immediate feedback
              if (token) {
                try {
                  const s = await checkCodeAndOpenConfirm(value);
                  toast({
                    title: s.redeemable ? t('redeemer.codeDetected') : t('redeemer.codeFound'),
                    description: s.redeemable
                      ? t('redeemer.codeBenefit', { title: (s.actionTitle || t('redeemer.action')), type: (s.discountType || ''), value: (s.discountValue ?? '') })
                      : (s.error || s.reason || t('redeemer.notRedeemable')),
                  });
                } catch (err) {
                  setScanInfo({ valid: false, error: err?.message || t('redeemer.invalid') });
                  toast({ title: t('redeemer.invalidCode'), description: err?.message || "" });
                }
              }
            }
          }
        } catch(_) {}
      }
      rafId = requestAnimationFrame(loop);
    }
    startCamera();
    return () => {
      mounted = false;
      if (rafId) cancelAnimationFrame(rafId);
      try { const tracks = stream?.getTracks?.() || []; tracks.forEach(t=>t.stop()); } catch {}
      setScanning(false);
      trackRef.current = null;
      setHasTorch(false);
      setTorchOn(false);
    };
  }, [scannerOpen, token]);

  // Lock body scroll when scanner overlay is open (mobile friendly)
  useEffect(() => {
    if (!scannerOpen) return;
    const bodyPrev = document.body.style.overflow;
    const htmlPrev = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = bodyPrev;
      document.documentElement.style.overflow = htmlPrev;
    };
  }, [scannerOpen]);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (_) {
      // ignore
    }
  };
  return (
    <div className="min-h-screen bg-muted">
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-xl font-semibold text-foreground">{t('redeemer.title')}</h1>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-background p-4 rounded shadow">
            <h3 className="font-semibold text-sm mb-2">{t('redeemer.qrTitle')}</h3>
            <p className="text-[11px] text-muted-foreground mb-3">{t('redeemer.qrHelp')}</p>
            <Button type="button" className="w-full h-12 text-base" onClick={() => { setCameraError(""); setScannerOpen(true); }}>
              <QrCode className="w-5 h-5 mr-2" /> {t('redeemer.scan')}
            </Button>
          </div>
          <form
       onSubmit={openConfirm}
          className="bg-background p-4 rounded shadow flex flex-col sm:flex-row gap-3 sm:items-end"
       >
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">{t('redeemer.enterCode')}</label>
            <Input
              placeholder={t('redeemer.codePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onBlur={onBlurNormalize}
              autoFocus
              ref={inputRef}
              className="h-12 text-base"
            />
            {scanInfo && (
              <div className="mt-2 text-[11px]">
                {scanInfo.valid === false && (
                  <span className="text-red-600">{scanInfo.error || t('redeemer.codeInvalid')}</span>
                )}
                {scanInfo.valid !== false && (
                  <span className={scanInfo.redeemable ? "text-green-700" : "text-yellow-700"}>
                    {scanInfo.redeemable
                      ? t('redeemer.redeemableWith', { type: scanInfo.discountType || '', value: (scanInfo.discountValue ?? '') })
                      : (scanInfo.reason || (scanInfo.redeemed ? t('redeemer.alreadyRedeemed') : t('redeemer.notRedeemable')))}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button type="button" variant="secondary" className="h-12 px-4 sm:px-6" onClick={pasteFromClipboard}>{t('redeemer.paste')}</Button>
            <Button type="submit" className="h-12 px-6 w-full sm:w-auto" disabled={loading || !code.trim()}>
              {loading ? t('redeemer.checking') : t('redeemer.check')}
            </Button>
          </div>
          </form>
        </div>
        {scannerOpen && createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/90 w-screen h-[100dvh]"
            style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', touchAction: 'none' }}
            onTouchMove={(e) => { e.preventDefault(); }}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-3 text-white text-sm">
                <div>{t('redeemer.camera')}</div>
                <div className="flex items-center gap-2">
                  {hasTorch && (
                    <button className="border px-3 py-1 rounded flex items-center gap-1" onClick={toggleTorch}>
                      {torchOn ? <FlashlightOff className="w-4 h-4"/> : <Flashlight className="w-4 h-4"/>}
                      <span className="hidden sm:inline">{t('redeemer.torch')}</span>
                    </button>
                  )}
                  <button className="border px-3 py-1 rounded" onClick={() => setScannerOpen(false)}>{t('redeemer.close')}</button>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              {cameraError && (
                <div className="p-3 text-center text-red-300 text-xs">{cameraError}</div>
              )}
            </div>
          </div>,
          document.body
        )}
        {confirmOpen && confirmData && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-background rounded shadow max-w-md w-full p-4">
              {(() => {
                const isStamp = !!confirmData.stampCard;
                const isStampRewardRedeem = isStamp && confirmData.stampIntent === 'REWARD_REDEEM';
                return (
                  <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">
                  {confirmData.redeemable
                    ? (isStamp ? (isStampRewardRedeem ? 'Belohnung einlösen' : 'Stempel verbuchen') : t('redeemer.confirm.redeemable'))
                    : (confirmData.redeemed ? t('redeemer.alreadyRedeemed') : t('redeemer.notRedeemable'))}
                </div>
                {!showRedeemSuccessAnim && (
                  <button className="text-xs border px-2 py-1 rounded" onClick={()=>setConfirmOpen(false)}>{t('redeemer.close')}</button>
                )}
              </div>
              {showRedeemSuccessAnim ? (
                <div className={`py-3 transition-opacity duration-500 ${redeemAnimFading ? "opacity-0" : "opacity-100"}`}>
                  <div className="flex justify-center">
                    <div className="w-48 h-48">
                      <DotLottieReact
                        src="https://lottie.host/77c4413f-852f-4b5f-80a7-e1863df7fac9/RaxkN1xThE.lottie"
                        autoplay
                        loop={false}
                      />
                    </div>
                  </div>
                  <div className="text-center -mt-2">
                    <div className="text-lg font-semibold text-green-700">Einlösung erfolgreich</div>
                    <div className="text-xs text-muted-foreground mt-1">Zusammenfassung wird angezeigt ...</div>
                  </div>
                </div>
              ) : (
              <div className="text-xs space-y-1">
                {confirmData.actionTitle && <div><span className="text-muted-foreground">{t('redeemer.labels.action')}:</span> {confirmData.actionTitle}</div>}
                {confirmData.campaignName && <div><span className="text-muted-foreground">{t('redeemer.labels.campaign')}:</span> {confirmData.campaignName}</div>}
                {(confirmData.endUserName || confirmData.endUserEmail) && (
                  <div><span className="text-muted-foreground">{t('redeemer.labels.user')}:</span> {confirmData.endUserName || confirmData.endUserEmail}</div>
                )}
                {isStamp && (
                  <div className="rounded border bg-blue-50 p-2 text-blue-900 mt-1">
                    <div className="font-medium mb-1">Vorgang: {isStampRewardRedeem ? 'Belohnung einlösen' : 'Stempel verbuchen'}</div>
                    <div className="font-medium">Fortschritt</div>
                    <div className="mt-1">
                      {(confirmData.stampProgressBefore ?? 0)}/{confirmData.stampTotal ?? "-"} {'->'} {(confirmData.stampProgressAfterPreview ?? confirmData.stampProgressBefore ?? 0)}/{confirmData.stampTotal ?? "-"}
                    </div>
                    <div className="text-[11px] text-blue-800 mt-1">
                      {isStampRewardRedeem
                        ? 'Erst nach Bestätigung wird die Belohnung als eingelöst markiert.'
                        : 'Erst nach Bestätigung wird der Fortschritt beim Kunden verbucht.'}
                    </div>
                  </div>
                )}
                {(isStamp
                  ? (confirmData.stampReward || confirmData.discountValue)
                  : (confirmData.discountType || confirmData.discountValue)) && (
                  <div>
                    <span className="text-muted-foreground">{t('redeemer.labels.benefit')}:</span>{' '}
                    {isStamp
                      ? (confirmData.stampReward || confirmData.discountValue)
                      : `${confirmData.discountType || ''} ${confirmData.discountValue ?? ''}`.trim()}
                  </div>
                )}
                {!confirmData.redeemable && confirmData.redeemedAt && (
                  <div className="text-yellow-700">{t('redeemer.redeemedAt', { time: new Date(confirmData.redeemedAt).toLocaleString() })}</div>
                )}
                {!confirmData.redeemable && !confirmData.redeemed && (confirmData.reason || confirmData.error) && (
                  <div className="text-yellow-700">{confirmData.reason || confirmData.error}</div>
                )}
              </div>
              )}
              {!showRedeemSuccessAnim && (
                <div className="mt-4 flex items-center justify-end gap-2">
                  {confirmData.redeemable && (
                    <Button onClick={()=>performRedeem(confirmData.code)} disabled={confirmBusy}>
                      {confirmBusy ? t('redeemer.redeeming') : (isStamp ? (isStampRewardRedeem ? 'Belohnung einlösen' : 'Stempel bestätigen') : t('redeemer.redeemNow'))}
                    </Button>
                  )}
                </div>
              )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
        <div className="bg-background p-4 rounded shadow">
          <h3 className="font-semibold text-sm mb-3">{t('redeemer.todayRedeems', { date: dateStr })}</h3>
          <div className="text-xs divide-y max-h-72 overflow-y-auto">
            {today ? (
              today.redeems
                .filter((r) => r.redeemedAt)
                .map((r) => (
                  <div key={r.id} className="py-2 flex justify-between">
                    <span className="truncate flex-1 mr-2 flex items-center gap-2">
                      <span>{r.actionTitle}</span>
                      {r.kind === 'STAMP_REWARD_REDEEM' && (
                        <span className="inline-flex items-center rounded bg-green-100 text-green-800 px-1.5 py-0.5 text-[10px]">
                          Belohnung eingelöst
                        </span>
                      )}
                    </span>
                    <span>{new Date(r.redeemedAt).toLocaleTimeString()}</span>
                  </div>
                ))
            ) : (
              <div className="py-4 text-muted-foreground">{t('common.loading')}</div>
            )}
            {today &&
              today.redeems.filter((r) => r.redeemedAt).length === 0 && (
                <div className="py-4 text-muted-foreground">{t('redeemer.none')}</div>
              )}
          </div>
        </div>

        {/* Laufende Aktionen/Kampagnen mit Einlösungen heute */}
        <div className="bg-background p-4 rounded shadow">
          <h3 className="font-semibold text-sm mb-3">{t('redeemer.runningByCampaign')}</h3>
          {(() => {
            const now = new Date();
            const isExpired = (a) => !!(a.redeemEnd || a.requestEnd) && new Date(a.redeemEnd || a.requestEnd) < now;
            const running = actions.filter(a => a.active && !isExpired(a));
            if (!running.length) return <div className="text-xs text-muted-foreground">{t('redeemer.noneRunning')}</div>;
            // Build per-action maps for today's SCANS and today's REDEEMS
            const redeemsToday = today?.redeems?.filter(r => r.redeemedAt) || [];
            const perActionRedeems = new Map();
            for (const r of redeemsToday) {
              const cur = perActionRedeems.get(r.actionId) || { count: 0, last: null, title: r.actionTitle };
              cur.count += 1;
              const t = new Date(r.redeemedAt);
              if (!cur.last || t > cur.last) cur.last = t;
              perActionRedeems.set(r.actionId, cur);
            }
            const byCampaign = new Map();
            for (const a of running) {
              const cId = a.campaignId;
              if (!byCampaign.has(cId)) byCampaign.set(cId, []);
              byCampaign.get(cId).push(a);
            }
            return (
              <div className="space-y-4">
                {[...byCampaign.entries()].map(([cid, list]) => (
                  <div key={cid} className="border rounded">
                    <div className="px-3 py-2 bg-muted text-[11px] font-semibold text-foreground flex items-center justify-between">
                      <span>{campaigns.find(c=>c.id===cid)?.name || t('redeemer.labels.campaign')}</span>
                      <span className="text-muted-foreground">{t('redeemer.actionsCount', { count: list.length })}</span>
                    </div>
                    <div className="divide-y text-[11px]">
                      {list.map(a => {
                        const statsRedeem = perActionRedeems.get(a.id) || { count: 0, last: null };
                        const totalRedeems = (a.redeemedCount ?? a._count?.redeems ?? 0);
                        const totalHolders = (a.holdersCount ?? 0);
                        return (
                          <button key={a.id} className="w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-muted" onClick={() => { setDetailFor(a); setDetailTab('today'); setPageToday(0); setPageAll(0); }}>
                            <div className="min-w-0">
                              <div className="font-medium truncate">{a.title}</div>
                              <div className="text-muted-foreground truncate">{a.discountType} {a.discountValue ?? ''}</div>
                            </div>
                            <div className="text-right whitespace-nowrap">
                              <div>{t('redeemer.today')}: {statsRedeem.count}</div>
                              <div>{t('redeemer.total')}: {totalRedeems}/{totalHolders}</div>
                              <div className="text-muted-foreground">{statsRedeem.last ? statsRedeem.last.toLocaleTimeString() : '—'}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {detailFor && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
            <div className="bg-background rounded shadow max-w-lg w-full p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold truncate">{detailFor.title}</div>
                <button className="text-xs border px-2 py-1 rounded" onClick={() => setDetailFor(null)}>{t('redeemer.close')}</button>
              </div>
              <div className="flex text-xs border rounded overflow-hidden mb-3">
                <button className={`px-3 py-1 ${detailTab==='today'?'bg-blue-600 text-white':'bg-background'}`} onClick={() => setDetailTab('today')}>{t('redeemer.today')}</button>
                <button className={`px-3 py-1 ${detailTab==='all'?'bg-blue-600 text-white':'bg-background'}`} onClick={() => setDetailTab('all')}>{t('redeemer.total')}</button>
              </div>
              {detailTab === 'today' ? (
                <div className="text-xs max-h-72 overflow-y-auto divide-y">
                  {listToday.items.map((it) => (
                    <div key={it.id} className="py-2 flex justify-between">
                      <span>{t('redeemer.redeemEntry', { email: (it.byEmail || '') }).trim()}</span>
                      <span>{new Date(it.at).toLocaleTimeString()}</span>
                    </div>
                  ))}
                  {listToday.items.length === 0 && <div className="py-6 text-center text-muted-foreground">{t('redeemer.noneToday')}</div>}
                </div>
              ) : (
                <div className="text-xs max-h-72 overflow-y-auto divide-y">
                  {listAll.items.map((it) => (
                    <div key={it.id} className="py-2 flex justify-between">
                      <span>{t('redeemer.redeemEntry', { email: (it.byEmail || '') }).trim()}</span>
                      <span>{new Date(it.at).toLocaleDateString()} {new Date(it.at).toLocaleTimeString()}</span>
                    </div>
                  ))}
                  {listAll.items.length === 0 && <div className="py-6 text-center text-muted-foreground">{t('redeemer.none')}</div>}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between text-xs">
                {detailTab==='today' ? (
                  <>
                    <span>{t('redeemer.pageOf', { page: (pageToday + 1), total: Math.max(1, Math.ceil(listToday.total / pageSize)) })}</span>
                    <div className="flex gap-2">
                      <button className="border px-2 py-1 rounded" disabled={pageToday===0} onClick={()=>setPageToday(p=>Math.max(0,p-1))}>{t('redeemer.prev')}</button>
                      <button className="border px-2 py-1 rounded" disabled={(pageToday+1)*pageSize>=listToday.total} onClick={()=>setPageToday(p=>p+1)}>{t('redeemer.next')}</button>
                    </div>
                  </>
                ) : (
                  <>
                    <span>{t('redeemer.pageOf', { page: (pageAll + 1), total: Math.max(1, Math.ceil(listAll.total / pageSize)) })}</span>
                    <div className="flex gap-2">
                      <button className="border px-2 py-1 rounded" disabled={pageAll===0} onClick={()=>setPageAll(p=>Math.max(0,p-1))}>{t('redeemer.prev')}</button>
                      <button className="border px-2 py-1 rounded" disabled={(pageAll+1)*pageSize>=listAll.total} onClick={()=>setPageAll(p=>p+1)}>{t('redeemer.next')}</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RedeemerDashboard;
